import React, { useState, useEffect } from 'react';
import { X, Container, AlertCircle, Loader } from 'lucide-react';
import { containerService } from '../../../services/containerService';

const AssignToContainerModal = ({ task, onClose, onAssign }) => {
  const [containers, setContainers] = useState([]);
  const [selectedContainerId, setSelectedContainerId] = useState('');
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadOpenContainers();
  }, []);

  const loadOpenContainers = async () => {
    try {
      setLoading(true);
      const data = await containerService?.getOpenContainers();
      setContainers(data);
    } catch (err) {
      console.error('Error loading containers:', err);
      setError('Erreur lors du chargement des conteneurs');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedContainerId) {
      setError('Veuillez sélectionner un conteneur');
      return;
    }

    if (!task?.shipmentId) {
      setError('Ce client n\'a pas encore d\'envoi créé');
      return;
    }

    try {
      setAssigning(true);
      setError('');
      await containerService?.assignShipmentToContainer(task?.shipmentId, selectedContainerId);
      onAssign();
      onClose();
    } catch (err) {
      console.error('Error assigning to container:', err);
      setError(err?.message || 'Erreur lors de l\'assignation au conteneur');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Container className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Assigner à un Conteneur</h2>
              <p className="text-sm text-gray-600">Client: {task?.clientName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Shipment Info */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Informations de l'envoi</h3>
          <div className="space-y-1 text-sm text-blue-800">
            <p><strong>Référence:</strong> {task?.shipment?.reference || 'N/A'}</p>
            <p><strong>Client:</strong> {task?.clientName}</p>
            <p><strong>Téléphone:</strong> {task?.clientPhone || 'N/A'}</p>
            <p><strong>Adresse:</strong> {task?.clientAddress}, {task?.clientCity}</p>
          </div>
        </div>

        {/* Container Selection */}
        {loading ? (
          <div className="py-8 text-center">
            <Loader className="w-8 h-8 text-gray-400 animate-spin mx-auto" />
            <p className="mt-2 text-sm text-gray-600">Chargement des conteneurs...</p>
          </div>
        ) : containers?.length === 0 ? (
          <div className="py-8 text-center">
            <Container className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">Aucun conteneur ouvert disponible</p>
            <p className="text-sm text-gray-500 mt-2">Créez un conteneur pour pouvoir assigner des envois</p>
          </div>
        ) : (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sélectionner un conteneur
            </label>
            <select
              value={selectedContainerId}
              onChange={(e) => setSelectedContainerId(e?.target?.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">-- Choisir un conteneur --</option>
              {containers?.map((container) => (
                <option key={container?.id} value={container?.id}>
                  {container?.containerCode} - {container?.departurePort} → {container?.destinationPort}
                  {container?.departureDate && ` (Départ: ${new Date(container?.departureDate)?.toLocaleDateString('fr-FR')})`}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-gray-500">
              Seuls les conteneurs ouverts (au port Le Havre/Anvers) sont affichés
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={assigning}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleAssign}
            disabled={assigning || !selectedContainerId || containers?.length === 0}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {assigning ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Assignation...
              </>
            ) : (
              <>
                <Container className="w-4 h-4" />
                Assigner au Conteneur
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignToContainerModal;